import { cloneDeep } from "lodash";

/**
 * A filter value that can travel as a single string — legacy single-select payloads and
 * filters already persisted in localStorage — or as a list, which is what the
 * multi-select controls now emit (area, zone, dealer/group value, vehicle type, engine
 * type, model).
 */
export type FilterValue = string | string[];

/**
 * Normalizes a value that may be a single string or a list of strings to a list,
 * dropping the `""` placeholder this payload uses to mean "no filter applied".
 */
export const toStringList = (
  value: string | string[] | null | undefined
): string[] => {
  const list = Array.isArray(value) ? value : value != null ? [value] : [];
  return list.filter((item) => typeof item === "string" && item.trim() !== "");
};

/**
 * True when a filter actually carries a selection. Use this instead of a bare truthiness
 * check: `""` is falsy but `[]` and `[""]` are not, so `if (value)` silently treats an
 * empty multi-select as "filter applied".
 */
export const hasFilterValue = (
  value: string | string[] | null | undefined
): boolean => toStringList(value).length > 0;

export interface ISubRegion {
  market: string[];
  subregion: string;
}

export class SubRegion implements ISubRegion {
  market: string[];
  subregion: string;
  constructor() {
    this.market = [];
    this.subregion = "";
  }
}

export interface IRegion {
  region: string;
  subRegions: ISubRegion[];
}
export class Region implements IRegion {
  region: string;
  subRegions: ISubRegion[];
  constructor() {
    (this.region = ""), (this.subRegions = []);
  }
}

export interface IGroup {
  type: string;
  value: FilterValue;
}

export class Group implements IGroup {
  type: string;
  value: FilterValue;
  constructor() {
    this.type = "";
    // group value is a multi-select (isMultipleSelect): default to the [""] "no filter"
    // array the network payload uses, not a bare "", so the very first request carries an array.
    this.value = [""];
  }
}
export interface INetwork {
  brand: string[];
  regions: IRegion[];
  distributor: string[];
  zone: FilterValue;
  area: FilterValue;
  dealer: string[];
  outlet: string[];
  agent: string[];
  group: IGroup;
  channel: string[];
  caseOrigin: string[];
  crisis: string[];
  recall: string[];
  salesChannel?: string[];
  networkType?: string;
}

export class Network implements INetwork {
  brand: string[];
  regions: IRegion[];
  distributor: string[];
  zone: FilterValue;
  area: FilterValue;
  dealer: string[];
  outlet: string[];
  agent: string[];
  group: IGroup;
  channel: string[];
  caseOrigin: string[];
  crisis: string[];
  recall: string[];
  type: string;
  salesChannel: string[]
  constructor() {
    this.brand = [];
    this.regions = [];
    this.distributor = [];
    // zone and area are multi-selects (isMultipleSelect): default to the [""] "no filter"
    // array the network payload uses, not a bare "", so the very first request carries an array.
    this.zone = [""];
    this.area = [""];
    this.dealer = [];
    this.outlet = [];
    this.agent = [];
    this.group = new Group();
    this.salesChannel = [];
    this.channel = [];
    this.caseOrigin = [];
    this.crisis = [];
    this.recall = [];
    this.type = "";
  }
}

export interface SalesChannel {
  id: string;
  market: string;
  description: string;
}

export interface IVehicle {
  model: FilterValue;
  modelType: FilterValue;
  engine: FilterValue;
}

export interface ICurrent {
  from: string;
  to: string;
}

export interface IPrevious {
  from: string;
  to: string;
}

export interface IPeriod {
  current: ICurrent;
  previous: IPrevious;
  datePreset: string;
}

export interface IQuestion {
  questionCode: string;
  section: string;
  tab: string;
  orderByScore: string;
  locale: string;
  limit: string;
}

type Nps = {
  totalSurvey: number;
}

export interface NpsFilter {
  current: Nps,
  previous?: Nps
}

export interface IAdditionalParameter {
  key: string;
  value: string[];
}

export interface IPayload {
  network: INetwork;
  vehicle: IVehicle;
  period: IPeriod;
  question: IQuestion;
  extraFilters?: any;
  additionalParameters?: IAdditionalParameter[];
  nps?: NpsFilter;
}

const networkNew: INetwork = {
  brand: [],
  regions: [],
  distributor: [],
  // zone, area and group.value are multi-selects (isMultipleSelect): default to the [""]
  // "no filter" array used across the network payload, not a bare "", so requests fired
  // before any selection (e.g. the first /getDealer call) carry an array.
  zone: [""],
  area: [""],
  dealer: [""],
  outlet: [""],
  agent: [""],
  group: {
    type: "",
    value: [""],
  },
  channel: [],
  caseOrigin: [],
  crisis: [],
  recall: [],
  salesChannel: [""]
};

const vehicleNew: IVehicle = {
  // model, modelType and engine are multi-selects (isMultipleSelect): default to an empty
  // array, not "", so requests fired before any selection carry [] rather than an empty string.
  model: [],
  modelType: [],
  engine: [],
};

const periodNew: IPeriod = {
  current: {
    from: "",
    to: "",
  },
  previous: {
    from: "",
    to: "",
  },
  datePreset: "",
};

const questionNew: IQuestion = {
  questionCode: "",
  section: "",
  tab: "",
  orderByScore: "",
  locale: "",
  limit: "",
};

export class Payload implements IPayload {
  network!: INetwork;
  vehicle: IVehicle;
  period: IPeriod;
  question: IQuestion;
  nps?: NpsFilter;
  market?: any;
  additionalParameters?: IAdditionalParameter[];

  constructor() {
    this.network = cloneDeep(networkNew);
    this.vehicle = cloneDeep(vehicleNew);
    this.period = cloneDeep(periodNew);
    this.question = cloneDeep(questionNew);
  }
}

export const isSection = (question: IQuestion, ...sections: string[]): boolean => sections.includes(question.section)
export const isTab = (question: IQuestion, ...tabs: string[]): boolean => tabs.includes(question.tab)
export const isTabAndSection = (question: IQuestion, ...query: string[]): boolean => isSection(question, ...query) && isTab(question, ...query)